import { Component } from '@theme/component';

class NaptonHolidayMap extends Component {
  base = 'napton';
  length = 'all';
  selections = { napton: 'route18', autherley: 'route7' };

  connectedCallback() {
    super.connectedCallback();
    this.abort?.abort();
    this.abort = new AbortController();
    this.addEventListener('click', this.handleClick, { signal: this.abort.signal });
    this.addEventListener('change', this.handleChange, { signal: this.abort.signal });
    this.refresh();
    this.labelObserver?.disconnect();
    this.labelObserver = new ResizeObserver(() => this.placeLabels());
    const scene = this.querySelector('.nrm-scene');
    if (scene instanceof HTMLElement) this.labelObserver.observe(scene);
  }

  updatedCallback() {
    super.updatedCallback();
    this.refresh();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.labelObserver?.disconnect();
    super.disconnectedCallback();
  }

  placeLabels() {
    const scene = this.querySelector('.nrm-scene');
    if (!(scene instanceof HTMLElement)) return;
    const bounds = scene.getBoundingClientRect();
    const occupied = [...this.querySelectorAll('[data-base-pin]:not([hidden])')].map(base => base.getBoundingClientRect());
    const stops = [...this.querySelectorAll('[data-map-stop-for]:not([hidden])')];
    for (const stop of stops) {
      if (!(stop instanceof HTMLElement) || !stop.offsetParent) continue;
      const label = stop.querySelector('.nrm-stop-name');
      if (!(label instanceof HTMLElement)) continue;
      const point = stop.getBoundingClientRect();
      const { width, height } = label.getBoundingClientRect();
      const preferred = ['right', 'left', 'top', 'bottom'].find(side => stop.classList.contains(`nrm-stop--${side}`)) || 'right';
      const sides = [preferred, ...['right', 'left', 'top', 'bottom'].filter(side => side !== preferred)];
      let best;
      for (const gap of [12, 20, 30, 42]) {
        for (const side of sides) {
          const x = side === 'right' ? gap : side === 'left' ? -width - gap : -width / 2;
          const y = side === 'bottom' ? gap : side === 'top' ? -height - gap : -height / 2;
          const rect = { left: point.left + x, right: point.left + x + width, top: point.top + y, bottom: point.top + y + height };
          const clipped = Math.max(0, bounds.left + 3 - rect.left) + Math.max(0, rect.right - bounds.right + 3) + Math.max(0, bounds.top + 3 - rect.top) + Math.max(0, rect.bottom - bounds.bottom + 3);
          const overlap = occupied.reduce((total, other) => total + Math.max(0, Math.min(rect.right + 3, other.right) - Math.max(rect.left - 3, other.left)) * Math.max(0, Math.min(rect.bottom + 3, other.bottom) - Math.max(rect.top - 3, other.top)), 0);
          const score = clipped * 10000 + overlap;
          if (!best || score < best.score) best = { x, y, rect, score };
          if (!score) break;
        }
        if (best && !best.score) break;
      }
      if (best) {
        label.style.transform = `translate(${best.x}px, ${best.y}px)`;
        occupied.push(best.rect);
      }
    }
  }

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button, a[data-map-find]');
    if (!button || !this.contains(button)) return;
    if (button.dataset.mapBase) {
      this.base = button.dataset.mapBase;
      this.refresh();
      this.querySelector('.nrm-route-list')?.scrollTo({ left: 0, behavior: 'instant' });
    } else if (button.dataset.mapLength) {
      this.length = button.dataset.mapLength;
      this.refresh();
      this.querySelector('.nrm-route-list')?.scrollTo({ left: 0, behavior: 'instant' });
    } else if (button.dataset.mapRoute) {
      this.selections[this.base] = button.dataset.mapRoute;
      this.refresh();
    } else if (button.dataset.mapScroll) {
      const list = this.querySelector('.nrm-route-list');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      list?.scrollBy({ left: Number(button.dataset.mapScroll) * list.clientWidth * 0.8, behavior: reduce ? 'instant' : 'smooth' });
    } else if (button.dataset.mapFind) {
      const value = button.dataset.mapFind === 'napton' ? 'Napton Marina' : 'Autherley Junction';
      const input = document.querySelector(`#nn-finder input[name="base_id"][value="${value}"]`);
      if (input instanceof HTMLInputElement) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  handleChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-map-select]')) return;
    const card = [...this.querySelectorAll('[data-map-route]')].find(card => card.dataset.mapRoute === select.value && !card.hidden);
    if (!card) return;
    this.selections[this.base] = select.value;
    this.refresh();
    const list = this.querySelector('.nrm-route-list');
    list?.scrollTo({ left: card.offsetLeft - list.offsetLeft, behavior: 'instant' });
  };

  refresh() {
    const cards = [...this.querySelectorAll('[data-map-route]')];
    const visible = cards.filter(card => {
      const durations = card.dataset.mapDuration.split(' ');
      const matchesLength = this.length === 'all' || durations.includes(this.length) || (this.length === 'long' && durations.includes('two'));
      card.hidden = card.dataset.mapRouteBase !== this.base || !matchesLength;
      return !card.hidden;
    });
    let selected = visible.find(card => card.dataset.mapRoute === this.selections[this.base]);
    if (!selected && visible.length) selected = visible[0];
    if (selected) this.selections[this.base] = selected.dataset.mapRoute;
    const chosen = selected?.dataset.mapRoute;
    this.dataset.activeBase = this.base;
    this.querySelectorAll('[data-map-art]').forEach(art => { art.hidden = art.dataset.mapArt !== this.base; });
    this.querySelectorAll('[data-map-base]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mapBase === this.base)));
    this.querySelectorAll('[data-map-length]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mapLength === this.length)));
    cards.forEach(card => card.setAttribute('aria-pressed', String(card.dataset.mapRoute === chosen)));
    const select = this.querySelector('[data-map-select]');
    if (select instanceof HTMLSelectElement) {
      [...select.options].forEach(option => {
        const available = visible.some(card => card.dataset.mapRoute === option.value);
        option.hidden = !available;
        option.disabled = !available;
      });
      select.value = chosen || '';
    }
    this.querySelectorAll('[data-map-panel]').forEach(panel => { panel.hidden = panel.dataset.mapPanel !== chosen; });
    this.querySelectorAll('[data-map-stop-for]').forEach(stop => { stop.hidden = stop.dataset.mapStopFor !== chosen; });
    this.querySelectorAll('[data-map-network]').forEach(network => { network.toggleAttribute('hidden', network.dataset.mapNetwork !== this.base); });
    this.querySelectorAll('[data-base-pin]').forEach(pin => { pin.hidden = pin.dataset.basePin !== this.base; });
    const path = selected?.dataset.mapGeometry || '';
    this.querySelector('[data-map-path]')?.setAttribute('d', path);
    this.querySelector('[data-map-edge]')?.setAttribute('d', path);
    this.querySelector('[data-map-empty]').hidden = visible.length > 0;
    const status = this.querySelector('[data-map-status]');
    if (status) status.textContent = `${this.dataset.countLabel.replace('[count]', visible.length)}${selected ? ': ' + selected.querySelector('strong').textContent : ''}`;
    this.placeLabels();
  }
}

if (!customElements.get('napton-holiday-map')) customElements.define('napton-holiday-map', NaptonHolidayMap);



